import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, MaskLines } from "../components/Reveal";
import { HERO, DESTINATIONS } from "../data/content";

const DIFFERENT = [
  { t: "Thoughtfully Curated Experiences", d: "Every journey is composed by hand — never pulled from a catalogue." },
  { t: "Dedicated Travel Specialists", d: "One expert guides you from first conversation to safe return." },
  { t: "Trusted Global Partnerships", d: "Access to the world's finest houses, villas, cruises and guides." },
  { t: "End-to-End Journey Management", d: "Flights, transfers, stays and moments — arranged as one seamless whole." },
  { t: "Personalized Service", d: "Designed entirely around your pace, taste and purpose." },
  { t: "Operational Excellence", d: "Precision behind the scenes so nothing interrupts the experience." },
  { t: "Discreet Support", d: "Quiet, confidential assistance, awake whenever you are." },
];

const PRINCIPLES = [
  { n: "01", t: "Thoughtful Curation", d: "We design with intent — every choice considered, nothing left to chance." },
  { n: "02", t: "Precision", d: "The smallest details are where extraordinary journeys are truly made." },
  { n: "03", t: "Integrity", d: "Honest counsel and transparent guidance at every step." },
  { n: "04", t: "Hospitality", d: "Genuine care that turns a journey into a feeling of home." },
  { n: "05", t: "Partnership", d: "Trusted relationships that open doors others cannot." },
  { n: "06", t: "Excellence", d: "An unwavering standard, from first enquiry to final farewell." },
];

function AboutHero() {
  const videoRef = useRef(null);
  useEffect(() => { if (videoRef.current) videoRef.current.playbackRate = 0.7; }, []);
  return (
    <section data-testid="about-hero" className="relative h-[100svh] overflow-hidden bg-navy">
      <video ref={videoRef} autoPlay muted loop playsInline poster={HERO.poster} className="absolute inset-0 w-full h-full object-cover">
        <source src={HERO.video} type="video/mp4" />
      </video>
      <div className="absolute inset-0" style={{ background: "rgba(13,16,56,0.6)" }} />
      <div className="relative h-full max-w-[1500px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-[14vh]">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 1 }} className="overline text-gold mb-6">About</motion.p>
        <h1 className="font-serif text-ivory text-5xl md:text-[6vw] leading-[0.98] pb-[0.08em] max-w-[18ch]">
          <MaskLines lines={["About End to End", "Destinations."]} delay={0.4} />
        </h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 1 }} className="font-serif italic text-2xl md:text-3xl text-gold mt-6">
          Curated Journeys. Exceptional Experiences.
        </motion.p>
      </div>
    </section>
  );
}

export default function About() {
  return (
    <main data-testid="about-page" className="bg-ivory">
      <AboutHero />

      {/* 02 — Our Story */}
      <section className="bg-ivory py-24 md:py-32" data-testid="about-story">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="relative h-[560px] overflow-hidden">
              <img src={DESTINATIONS[9].hero} alt="Our story" loading="lazy" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="overline text-charcoal/50 mb-6">Our Story</p>
            <p className="font-serif text-3xl md:text-4xl text-navy leading-[1.25]">A travel house devoted to curating journeys that inspire, connect, and endure.</p>
            <p className="text-charcoal/70 mt-8 leading-relaxed text-[15px]">
              End to End Destinations was founded on a simple belief — that travel, at its finest, is not a transaction but a transformation. We craft personalised journeys for discerning travellers and considered group experiences for institutions, blending luxury, authenticity and effortless comfort.
            </p>
            <p className="text-charcoal/70 mt-5 leading-relaxed text-[15px]">
              From the first conversation to the final farewell, every detail is designed, coordinated and cared for — so that all you carry home is the experience itself.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 03 — Philosophy */}
      <section className="bg-navy py-28 md:py-44 text-center" data-testid="about-philosophy">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="overline text-gold mb-8">Philosophy</p>
            <h2 className="font-serif text-ivory text-5xl md:text-8xl leading-[1.02]">Travel, Thoughtfully Curated.</h2>
          </Reveal>
        </div>
      </section>

      {/* 04 — Privé & Collective */}
      <section className="grid md:grid-cols-2" data-testid="about-divisions">
        {[
          { name: "End to End Destinations Privé", sub: "Bespoke Luxury Travel", d: "Private, discreet journeys designed entirely around one traveller — the world's finest hotels, villas, cruises and experiences, arranged with intent.", img: DESTINATIONS[0].hero, to: "/prive" },
          { name: "Collective", sub: "Professional Group Travel & Mobility Solutions", d: "Considered group travel for institutions, corporates and celebrations — strategy, logistics and on-ground coordination handled end to end.", img: DESTINATIONS[4].hero, to: "/collective" },
        ].map((s) => (
          <div key={s.name} className="relative min-h-[70vh] overflow-hidden group">
            <img src={s.img} alt={s.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105" />
            <div className="absolute inset-0" style={{ background: "rgba(13,16,56,0.72)" }} />
            <div className="relative h-full flex flex-col justify-center px-8 md:px-14 py-16">
              <p className="overline text-gold mb-5">{s.sub}</p>
              <h3 className="font-serif text-4xl md:text-5xl text-ivory leading-tight">{s.name}</h3>
              <p className="text-ivory/70 mt-6 max-w-md leading-relaxed">{s.d}</p>
              <Link to={s.to} className="btn-ghost text-ivory mt-10 w-fit hover:border-gold">Explore <ArrowRight size={16} /></Link>
            </div>
          </div>
        ))}
      </section>

      {/* 05 — Why We Are Different */}
      <section className="bg-ivory py-24 md:py-32" data-testid="about-different">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10">
          <Reveal className="mb-14"><p className="overline text-charcoal/50">Why We Are Different</p>
            <h2 className="font-serif text-4xl md:text-6xl text-navy mt-5">The difference is in the care.</h2></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-navy/10">
            {DIFFERENT.map((c, i) => (
              <Reveal key={c.t} delay={(i % 3) * 0.06} className="bg-ivory">
                <div className="p-8 h-full">
                  <p className="font-serif text-gold text-xl">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="font-serif text-2xl text-navy mt-4">{c.t}</h3>
                  <p className="text-charcoal/60 mt-3 leading-relaxed text-[15px]">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 06 — Our Principles */}
      <section className="bg-navy py-24 md:py-32" data-testid="about-principles">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10">
          <Reveal className="mb-14"><p className="overline text-gold">Our Principles</p>
            <h2 className="font-serif text-4xl md:text-6xl text-ivory mt-5">What guides every journey.</h2></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.t} delay={(i % 3) * 0.06}>
                <div className="border border-ivory/10 p-8 h-full hover:border-gold/40 transition-colors">
                  <p className="font-serif text-gold text-lg">{p.n}</p>
                  <h3 className="font-serif text-2xl text-ivory mt-4">{p.t}</h3>
                  <p className="text-ivory/60 mt-3 leading-relaxed text-[15px]">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 07 — Vision & Mission */}
      <section className="bg-ivory py-24 md:py-32" data-testid="about-vision">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-16">
          <Reveal>
            <p className="overline text-charcoal/50 mb-6">Our Vision</p>
            <p className="font-serif text-3xl md:text-4xl text-navy leading-[1.25]">To become one of the world&rsquo;s most respected travel houses — trusted for the artistry, integrity and care we bring to every journey.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="overline text-charcoal/50 mb-6">Our Mission</p>
            <p className="font-serif text-3xl md:text-4xl text-navy leading-[1.25]">To craft journeys that enrich lives — designing experiences of rare beauty and effortless comfort, delivered with precision from beginning to end.</p>
          </Reveal>
        </div>
      </section>

      {/* 08 — Signature Statement */}
      <section className="relative py-32 md:py-52 overflow-hidden bg-navy text-center" data-testid="about-signature">
        <img src={DESTINATIONS[7].hero} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-30 animate-kenburns" />
        <div className="absolute inset-0" style={{ background: "rgba(13,16,56,0.7)" }} />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal><h2 className="font-serif text-ivory text-4xl md:text-7xl leading-[1.1]">Every Journey Begins with Understanding.</h2></Reveal>
          <Reveal delay={0.15}><h2 className="font-serif italic text-gold text-4xl md:text-7xl leading-[1.1] mt-8">Every Experience Ends with a Story Worth Remembering.</h2></Reveal>
        </div>
      </section>

      {/* 09 — Begin Your Journey */}
      <section className="bg-ivory py-28 md:py-40 text-center" data-testid="about-cta">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="overline text-charcoal/50 mb-6">Begin Your Journey</p>
            <h2 className="font-serif text-navy text-5xl md:text-7xl max-w-[18ch] mx-auto leading-[1.05]">Every remarkable journey begins with a conversation.</h2>
            <Link to="/enquire" data-testid="about-begin-cta" className="btn-gold mt-12">Begin Your Journey <ArrowRight size={16} /></Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
